/**
 * DriveZone — ResultCard
 * Displays one city result from a proximity search.
 * Shows exact_match styling (green ✓) vs widened result styling (amber ~).
 */

import { Clock, Route, CheckCircle, AlertCircle, TriangleAlert } from 'lucide-react'
import type { CityResult } from '@/stores/searchStore'

interface ResultCardProps {
  result: CityResult
  rank: number
  maxMinutes?: number   // original requested max — used to show how far outside range
}

export default function ResultCard({ result, rank, maxMinutes }: ResultCardProps) {
  const exact = result.exact_match

  function outsideLabel(): string {
    if (!maxMinutes) return 'Outside requested range'
    const over = result.drive_minutes - maxMinutes
    if (over <= 0) return 'Outside requested range'   // below min case
    if (over <= 5)  return `${over} min over your max`
    if (over <= 10) return `${over} min over your max`
    return `${over} min outside your range`
  }

  return (
    <div
      className={`
        card p-4 transition-shadow duration-150 hover:shadow-card-hover
        ${exact ? 'border-slate-200' : 'border-amber-200 bg-amber-50/30'}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        {/* City name + rank */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700
                           text-xs font-bold flex items-center justify-center">
            {rank}
          </span>
          <span className="font-semibold text-slate-900 truncate text-sm">
            {result.name}
          </span>
        </div>

        {/* Exact match badge */}
        {exact ? (
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        )}
      </div>

      {/* Drive stats */}
      <div className="flex items-center gap-4 mt-2.5">
        <span className="flex items-center gap-1.5 text-sm text-slate-600">
          <Clock className="w-3.5 h-3.5 text-slate-400" />
          <span className={`font-medium ${exact ? 'text-slate-800' : 'text-amber-700'}`}>
            {exact ? '' : '~'}{result.drive_minutes} min
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-sm text-slate-500">
          <Route className="w-3.5 h-3.5 text-slate-400" />
          {result.drive_km} km
        </span>
      </div>

      {/* Widened result label */}
      {!exact && (
        <p className="mt-2 text-xs text-amber-600 font-medium">
          {outsideLabel()}
        </p>
      )}

      {/* Road disruption warning */}
      {result.disruption && (() => {
        const d = result.disruption
        const styles = {
          closed: 'bg-red-50 border-red-200 text-red-700',
          major:  'bg-orange-50 border-orange-200 text-orange-700',
          minor:  'bg-yellow-50 border-yellow-200 text-yellow-700',
        }
        const cls = styles[d.severity] ?? styles.minor
        return (
          <div className={`mt-2.5 flex items-start gap-2 rounded-lg border px-2.5 py-2 ${cls}`}>
            <TriangleAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-semibold leading-tight">{d.title}</p>
              {d.description && (
                <p className="text-xs opacity-80 mt-0.5 leading-tight">{d.description}</p>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
